/**
 * QS-58 — responsive layout for the NumericChart Likert scale-axis row.
 *
 * The scale anchors (min / midpoint / max) sit under the average-bar track. At the
 * wide deck/export width (~820px) they read cleanly: the endpoints anchor left/right
 * and a centered midpoint label floats above the dashed midpoint guide. But the same
 * SHARED component is embedded in the in-app report panel's NARROW right-hand column,
 * where the absolutely-positioned centered midpoint label collides with the endpoint
 * labels into an unreadable stack and the fixed gutter padding fights the column.
 *
 * This module isolates the (pure, testable) layout decision so the component stays a
 * thin renderer. It is CONTAINER-RELATIVE on purpose: the threshold is compared to the
 * chart's own measured width, NOT the viewport, so the wide deck never trips it and the
 * wide layout is a byte-for-byte no-op (see getScaleAxisLayout: a wide / unknown width
 * returns exactly the pre-QS-58 class strings).
 */

/**
 * Width (px) at/under which the scale axis switches to its narrow layout. Chosen so the
 * three anchors ("Very unlikely" / "Midpoint (3.0)" / "Very likely") cannot fit on one
 * row without colliding. The in-app report panel's right-hand column is ~405px, so the
 * threshold must sit ABOVE that (QS-58: at 360 the report column still collided). The
 * wide deck content area's scale-axis track (~820px minus the pl-36/pr-20 gutters ≈ 596px,
 * and wider on export) stays well above this, so the deck/export rendering is unchanged.
 */
export const NUMERIC_NARROW_WIDTH_PX = 420

/**
 * True when the chart is narrow enough that the inline three-anchor scale row would
 * overlap. `undefined` (not yet measured / SSR) is treated as wide so the first paint —
 * and any non-browser render — matches the established wide layout exactly.
 */
export function isNarrowNumericChart(width: number | undefined | null): boolean {
  if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) return false
  return width <= NUMERIC_NARROW_WIDTH_PX
}

export interface ScaleAxisLayout {
  /** Narrow mode active (drives structure: midpoint stacks below the endpoints). */
  isNarrow: boolean
  /** Whether to render the midpoint as its own stacked row under the endpoints. When
   *  false the midpoint keeps its original absolutely-centered position (wide layout). */
  stackMidpoint: boolean
  /** Classes for the endpoint <span>s. Narrow adds a max-width + truncation so a long
   *  anchor ("Very unlikely") can't shove its sibling off the track. Empty at wide. */
  endpointClassName: string
  /** Classes for the inline (wide) absolutely-centered midpoint span. Narrow hides it
   *  (the stacked row renders instead). */
  inlineMidpointClassName: string
  /** Classes for the narrow stacked midpoint row. Empty/unused at wide. */
  stackedMidpointClassName: string
}

/**
 * Resolve the scale-axis layout for a measured container width.
 *
 * Wide / unknown width → the original layout: no endpoint truncation, the inline
 * centered midpoint span is shown, no stacked row. This is the non-regression contract.
 *
 * Narrow width → endpoints get `max-w-[45%] truncate` so they share the row without
 * colliding, the inline midpoint is removed from flow (`hidden`), and the midpoint
 * renders as a centered row stacked beneath the endpoints.
 */
export function getScaleAxisLayout(width: number | undefined | null): ScaleAxisLayout {
  const isNarrow = isNarrowNumericChart(width)

  if (!isNarrow) {
    return {
      isNarrow: false,
      stackMidpoint: false,
      endpointClassName: '',
      inlineMidpointClassName: '',
      stackedMidpointClassName: '',
    }
  }

  return {
    isNarrow: true,
    stackMidpoint: true,
    // Cap each endpoint so a long label truncates instead of overlapping its sibling.
    // `min-w-0` lets the span actually shrink inside the justify-between flex row.
    endpointClassName: 'min-w-0 max-w-[45%] truncate',
    // Pull the absolutely-centered inline midpoint out of the layout in narrow mode.
    inlineMidpointClassName: 'hidden',
    // The stacked midpoint sits centered on its own line below the endpoints.
    stackedMidpointClassName: 'mt-0.5 text-center text-muted-foreground/60',
  }
}
